<script setup lang="ts">
import { ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Mail, Lock, ArrowLeft } from 'lucide-vue-next';
import { authAPI } from '../services/api';
import { useAuth } from '../composables/useAuth';
import { usePushNotifications } from '../composables/usePushNotifications';

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const router = useRouter();
const { t } = useI18n();
const { fetchUser } = useAuth();
const { registerPush } = usePushNotifications();

const formData = ref<FormData>({
  email: '',
  password: '',
  rememberMe: false,
});

const isLoading = ref(false);
const error = ref('');
const showTwoFA = ref(false);
const tfaDigits = ref(['', '', '', '', '', '']);
const tfaInputs = ref<HTMLInputElement[]>([]);

const handleSubmit = async (e: Event) => {
  e.preventDefault();
  if (formData.value.email && formData.value.password) {
    isLoading.value = true;
    error.value = '';

    try {
      const response = await authAPI.login({
        email: formData.value.email,
        password: formData.value.password,
      });

      if (response.twofa_required) {
        showTwoFA.value = true;
        await nextTick();
        tfaInputs.value[0]?.focus();
        return;
      }

      await completeLogin(response.token!);
    } catch (err: any) {
      handleError(err);
    } finally {
      isLoading.value = false;
    }
  }
};

const handleTwoFADigit = async (index: number, event: Event) => {
  const input = event.target as HTMLInputElement;
  const value = input.value.replace(/\D/g, '').slice(-1);
  tfaDigits.value[index] = value;

  if (value && index < 5) {
    tfaInputs.value[index + 1]?.focus();
  }

  if (tfaDigits.value.every((d) => d !== '')) {
    await submitTwoFA();
  }
};

const handleTwoFAKeydown = (index: number, event: KeyboardEvent) => {
  if (event.key === 'Backspace' && !tfaDigits.value[index] && index > 0) {
    tfaInputs.value[index - 1]?.focus();
  }
};

const handleTwoFAPaste = async (event: ClipboardEvent) => {
  event.preventDefault();
  const pasted = event.clipboardData?.getData('text') ?? '';
  const digits = pasted.replace(/\D/g, '').slice(0, 6).split('');
  digits.forEach((d, i) => {
    if (i < 6) tfaDigits.value[i] = d;
  });
  await nextTick();
  const nextEmpty = tfaDigits.value.findIndex((d) => d === '');
  const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
  tfaInputs.value[focusIndex]?.focus();

  if (tfaDigits.value.every((d) => d !== '')) {
    await submitTwoFA();
  }
};

const submitTwoFA = async () => {
  isLoading.value = true;
  error.value = '';

  try {
    const code = tfaDigits.value.join('');
    const response = await authAPI.login({
      email: formData.value.email,
      password: formData.value.password,
      code,
    });

    await completeLogin(response.token!);
  } catch (err: any) {
    tfaDigits.value = ['', '', '', '', '', ''];
    await nextTick();
    tfaInputs.value[0]?.focus();
    if (err.response?.status === 401) {
      error.value = t('login.error_invalid_code');
    } else {
      handleError(err);
    }
  } finally {
    isLoading.value = false;
  }
};

const completeLogin = async (token: string) => {
  localStorage.setItem('token', token);
  const userData = await fetchUser();
  if (userData) {
    registerPush().catch(() => {});
    router.push('/messenger');
  } else {
    error.value = t('login.error_fetch_user');
    localStorage.removeItem('token');
  }
};

const handleError = (err: any) => {
  console.error('Login error:', err);
  if (err.response?.status === 429) {
    const blockedUntil = err.response?.data?.blocked_until;
    if (blockedUntil) {
      const time = new Date(blockedUntil).toLocaleTimeString('pl-PL', {
        hour: '2-digit',
        minute: '2-digit',
      });
      error.value = t('login.error_too_many_attempts_with_time', { time });
    } else {
      error.value = t('login.error_too_many_attempts');
    }
  } else {
    error.value = err.response?.data?.message || t('login.error_invalid_credentials');
  }
};

const backToLogin = () => {
  showTwoFA.value = false;
  tfaDigits.value = ['', '', '', '', '', ''];
  error.value = '';
};

const handleForgotPassword = () => {
  router.push('/reset-password');
};

const handleSignUp = () => {
  router.push('/signup');
};

const isFormValid = () => {
  return formData.value.email && formData.value.password.length >= 6;
};
</script>

<template>
  <div
    class="w-full max-w-md bg-[#111113] rounded-3xl shadow-sm border border-[#2A2018] p-8 md:p-10"
  >
    <!-- 2FA View -->
    <template v-if="showTwoFA">
      <div class="mb-8 text-center">
        <h1 class="text-2xl text-[#F5F0EA] mb-2">
          {{ $t('login.twofa_title') }}
        </h1>
        <p class="text-[#9E9080] text-sm">{{ $t('login.twofa_subtitle') }}</p>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-5 p-4 bg-red-900/20 border border-red-800/40 rounded-2xl">
        <p class="text-sm text-red-400">{{ error }}</p>
      </div>

      <!-- 6-digit input -->
      <div class="flex gap-3 justify-center mb-8">
        <input
          v-for="(_, index) in tfaDigits"
          :key="index"
          :ref="
            (el) => {
              if (el) tfaInputs[index] = el as HTMLInputElement;
            }
          "
          type="text"
          inputmode="numeric"
          maxlength="1"
          :value="tfaDigits[index]"
          :disabled="isLoading"
          @input="handleTwoFADigit(index, $event)"
          @keydown="handleTwoFAKeydown(index, $event)"
          @paste="handleTwoFAPaste"
          class="w-12 h-14 text-center text-xl text-[#F5F0EA] bg-[#18181C] border border-[#2A2018] rounded-2xl focus:bg-[#1E1E23] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      <!-- Loading indicator -->
      <p v-if="isLoading" class="text-center text-sm text-[#9E9080] mb-6">
        {{ $t('login.submitting') }}
      </p>

      <!-- Back button -->
      <button
        type="button"
        @click="backToLogin"
        :disabled="isLoading"
        class="w-full flex items-center justify-center gap-2 px-5 py-3 text-sm text-[#9E9080] hover:text-[#F5F0EA] disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        <ArrowLeft class="w-4 h-4" />
        {{ $t('login.twofa_back') }}
      </button>
    </template>

    <!-- Login Form View -->
    <template v-else>
      <!-- Header -->
      <div class="mb-8 text-center">
        <h1 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('login.title') }}</h1>
        <p class="text-[#9E9080]">{{ $t('login.subtitle') }}</p>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-5 p-4 bg-red-900/20 border border-red-800/40 rounded-2xl">
        <p class="text-sm text-red-400">{{ error }}</p>
      </div>

      <!-- Login Form -->
      <form @submit="handleSubmit" class="space-y-5">
        <!-- Email Field -->
        <div>
          <label for="email" class="block text-sm text-[#F5F0EA] mb-2">{{
            $t('login.email_label')
          }}</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail class="w-4 h-4 text-[#524840]" />
            </div>
            <input
              type="email"
              id="email"
              required
              v-model="formData.email"
              class="w-full pl-11 pr-4 py-3 bg-[#18181C] border border-[#2A2018] rounded-2xl focus:bg-[#1E1E23] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition text-[#F5F0EA] placeholder:text-[#524840]"
              :placeholder="$t('login.email_placeholder')"
            />
          </div>
        </div>

        <!-- Password Field -->
        <div>
          <label for="password" class="block text-sm text-[#F5F0EA] mb-2">{{
            $t('login.password_label')
          }}</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock class="w-4 h-4 text-[#524840]" />
            </div>
            <input
              type="password"
              id="password"
              required
              minlength="6"
              v-model="formData.password"
              class="w-full pl-11 pr-4 py-3 bg-[#18181C] border border-[#2A2018] rounded-2xl focus:bg-[#1E1E23] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition text-[#F5F0EA] placeholder:text-[#524840]"
              :placeholder="$t('login.password_placeholder')"
            />
          </div>
        </div>

        <!-- Remember Me & Forgot Password -->
        <div class="flex items-center justify-between">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="formData.rememberMe"
              class="w-4 h-4 rounded border-[#2A2018] accent-[#D4956A] focus:ring-2 focus:ring-[#D4956A] cursor-pointer"
            />
            <span class="text-sm text-[#9E9080]">{{ $t('login.remember_me') }}</span>
          </label>

          <button
            type="button"
            @click="handleForgotPassword"
            class="text-sm text-[#D4956A] hover:underline"
          >
            {{ $t('login.forgot_password') }}
          </button>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="!isFormValid() || isLoading"
          class="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#D4956A] text-[#0A0A0B] rounded-2xl hover:bg-[#E8B48A] disabled:bg-[#18181C] disabled:text-[#524840] disabled:cursor-not-allowed transition font-medium"
        >
          <span v-if="isLoading">{{ $t('login.submitting') }}</span>
          <template v-else>
            {{ $t('login.submit') }}
          </template>
        </button>
      </form>

      <!-- Sign Up Link -->
      <p class="mt-8 text-center text-sm text-[#9E9080]">
        {{ $t('login.no_account') }}
        <button @click="handleSignUp" class="text-[#D4956A] hover:underline">
          {{ $t('login.sign_up') }}
        </button>
      </p>
    </template>
  </div>
</template>
